/**
 * MobileScoutingHeader Container Component
 *
 * Builds custom circular trigger buttons for the activity and menu popovers,
 * then passes the fully-rendered popovers to the view layer.
 * @module features/scouting/components/MobileScoutingHeader/MobileScoutingHeaderContainer
 */

import { Bell, Settings } from "lucide-react-native";
import React, { useCallback } from "react";

import MobileScoutingHeaderView from "./MobileScoutingHeaderView";

import HeaderActivityPopover from "@/components/organisms/HeaderActivityPopover";
import HeaderPopover from "@/components/organisms/HeaderPopover";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { TEST_ID_MENU_HEADER_BUTTON } from "e2e/testId";

/**
 * Renders a circular 40x40 icon button used as a popover trigger.
 * @param triggerProps - Props forwarded by the Popover component
 * @param IconComponent - Lucide icon to render inside the circle
 * @param label - Accessibility label for the button
 * @param testID - Test identity, forwarded so e2e can anchor on an id rather
 *   than on the visible label (see the SE-6812 note on the menu trigger below)
 * @returns Circular pressable trigger element
 */
const renderCircularTrigger = (
  triggerProps: object,
  IconComponent: React.ElementType,
  label: string,
  testID?: string
): React.JSX.Element => (
  <Pressable
    {...triggerProps}
    className="size-10 items-center justify-center rounded-full bg-content-muted/25"
    accessibilityLabel={label}
    accessibilityRole="button"
    testID={testID}
  >
    <Icon as={IconComponent} size="lg" className="text-content-primary" />
  </Pressable>
);

/**
 * Container for the mobile scouting header.
 * Wires up custom circular triggers for both popovers.
 */
const MobileScoutingHeaderContainer = () => {
  const renderActivityTrigger = useCallback(
    (triggerProps: object) =>
      renderCircularTrigger(triggerProps, Bell, "View activity notifications"),
    []
  );

  // SE-6812: this custom trigger replaces HeaderPopover's default one, which
  // carries TEST_ID_MENU_HEADER_BUTTON. Without forwarding that id the mobile
  // menu had NO stable test identity — only the visible label, which reads
  // "Open settings menu" here versus "Open menu" on the default trigger. Every
  // native flow that opened the menu by label broke when this header shipped
  // (settings-screens, logout, tabs-and-menu, on both platforms). Keep the id.
  const renderMenuTrigger = useCallback(
    (triggerProps: object) =>
      renderCircularTrigger(
        triggerProps,
        Settings,
        "Open settings menu",
        TEST_ID_MENU_HEADER_BUTTON
      ),
    []
  );

  return (
    <MobileScoutingHeaderView
      activityPopover={
        <HeaderActivityPopover renderTrigger={renderActivityTrigger} />
      }
      menuPopover={<HeaderPopover renderTrigger={renderMenuTrigger} />}
    />
  );
};

MobileScoutingHeaderContainer.displayName = "MobileScoutingHeaderContainer";

export default MobileScoutingHeaderContainer;
